// ==================== CUSTOMER SUB-ACCOUNTS MODAL (ADMIN) ====================
// The super admin manages a customer's sub-accounts.
// List view + an inline form panel (same pattern as ViewMembersModal / AddMembersPanel).

import { useState, useMemo } from 'react';
import {
  Search,
  UsersRound,
  UserPlus,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import { Modal, Button, Select } from '../../components/ui';
import type { SelectOption } from '../../components/ui/Select';
import {
  SubAccountItem,
  SubAccountForm,
  SubAccountConfirmDialogs,
  type SubAccountFormMode,
} from '../../components/sub-accounts';
import { useSubAccounts } from '../../hooks/useSubAccounts';
import { adminSubAccountsService, type SubAccount } from '../../services/sub-accounts.service';
import type { CustomerOrganization } from '../../services';

// ==================== FILTER OPTIONS ====================

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Sub-accounts' },
  { value: 'active', label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' },
];

// ==================== TYPES ====================

interface CustomerSubAccountsModalProps {
  customer: CustomerOrganization | null;
  isOpen: boolean;
  onClose: () => void;
}

interface SubAccountsModalContentProps {
  customer: CustomerOrganization;
  isOpen: boolean;
  onClose: () => void;
}

// ==================== MODAL CONTENT ====================

const SubAccountsModalContent = ({ customer, isOpen, onClose }: SubAccountsModalContentProps) => {
  // Stable per customer, so the list isn't refetched on unrelated re-renders
  const api = useMemo(() => adminSubAccountsService.forCustomer(customer.id), [customer.id]);

  const {
    subAccounts,
    loading,
    error,
    setError,
    success,
    showSuccess,
    fetchSubAccounts,
    deleteConfirm,
    setDeleteConfirm,
    deleting,
    handleDelete,
    toggleConfirm,
    setToggleConfirm,
    toggling,
    confirmToggleStatus,
  } = useSubAccounts(api);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form panel
  const [formMode, setFormMode] = useState<SubAccountFormMode | null>(null);
  const [selectedSubAccount, setSelectedSubAccount] = useState<SubAccount | null>(null);

  // ==================== FILTERED SUB-ACCOUNTS ====================

  const filteredSubAccounts = useMemo(() => {
    return subAccounts.filter((subAccount) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const name = `${subAccount.firstName} ${subAccount.lastName}`.toLowerCase();
        if (!name.includes(query) && !subAccount.email.toLowerCase().includes(query)) {
          return false;
        }
      }

      if (statusFilter === 'active' && !subAccount.isActive) return false;
      if (statusFilter === 'inactive' && subAccount.isActive) return false;

      return true;
    });
  }, [subAccounts, searchQuery, statusFilter]);

  // ==================== HANDLERS ====================

  const handleOpenForm = (mode: SubAccountFormMode, subAccount?: SubAccount) => {
    setSelectedSubAccount(subAccount || null);
    setFormMode(mode);
  };

  const handleCloseForm = () => {
    setFormMode(null);
    setSelectedSubAccount(null);
  };

  const handleSaved = (message: string) => {
    handleCloseForm();
    showSuccess(message);
    fetchSubAccounts();
  };

  const handleStatusFilter = (value: string | string[]) => {
    setStatusFilter(Array.isArray(value) ? value[0] : value);
  };

  // ==================== RENDER ====================

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-orange-500/10">
              <UsersRound className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <span className="text-white">{customer.businessName} Sub-accounts</span>
            </div>
          </div>
        }
        size="lg"
        contentClassName="px-6 py-4"
        footer={
          !formMode ? (
            <div className="flex items-center justify-between w-full">
              {/* Left side - Count */}
              <div className="text-sm text-(--color-muted-gray)">
                <span>{subAccounts.length} sub-account{subAccounts.length !== 1 ? 's' : ''} total</span>
              </div>

              {/* Right side - Actions */}
              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleOpenForm('create')}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Sub-account
                </Button>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          ) : undefined
        }
      >
        {/* Conditional Content */}
        {formMode ? (
          <SubAccountForm
            key={`${formMode}-${selectedSubAccount?.id || 'new'}`}
            mode={formMode}
            subAccount={selectedSubAccount}
            api={api}
            onCancel={handleCloseForm}
            onSaved={handleSaved}
            variant="panel"
          />
        ) : (
          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="flex items-center gap-3 relative z-10">
              <div className="flex-1 min-w-0 relative z-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-muted-gray) pointer-events-none z-10" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-11 px-4 pl-10 text-sm text-white bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-lg placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
                />
              </div>
              <div className="relative z-20 shrink-0 w-44">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  fullWidth
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchSubAccounts}
                disabled={loading}
                className="shrink-0"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Success Message */}
            {success && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <p className="text-sm text-emerald-300">{success}</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <p className="text-sm text-red-300">{error}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                  className="ml-auto"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                <p className="text-sm text-(--color-muted-gray) mt-3">Loading sub-accounts...</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && filteredSubAccounts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                  <UsersRound className="w-8 h-8 text-(--color-muted-gray)" />
                </div>
                <p className="text-white font-medium mb-1">No sub-accounts found</p>
                <p className="text-sm text-(--color-muted-gray) text-center max-w-sm">
                  {searchQuery || statusFilter !== 'all'
                    ? 'No sub-accounts match your current filters. Try adjusting your search.'
                    : 'This customer has no sub-accounts yet. Add one to give a team member access to assigned contacts and WhatsApp chat.'}
                </p>
              </div>
            )}

            {/* Sub-accounts List */}
            {!loading && filteredSubAccounts.length > 0 && (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {filteredSubAccounts.map((subAccount) => (
                  <SubAccountItem
                    key={subAccount.id}
                    subAccount={subAccount}
                    variant="card"
                    onEdit={() => handleOpenForm('edit', subAccount)}
                    onSetPassword={() => handleOpenForm('password', subAccount)}
                    onToggleStatus={() => setToggleConfirm(subAccount)}
                    onDelete={() => setDeleteConfirm(subAccount)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      <SubAccountConfirmDialogs
        deleteConfirm={deleteConfirm}
        onCloseDelete={() => setDeleteConfirm(null)}
        onConfirmDelete={handleDelete}
        deleting={deleting}
        toggleConfirm={toggleConfirm}
        onCloseToggle={() => setToggleConfirm(null)}
        onConfirmToggle={confirmToggleStatus}
        toggling={toggling}
      />
    </>
  );
};

// ==================== CUSTOMER SUB-ACCOUNTS MODAL ====================

export const CustomerSubAccountsModal = ({ customer, isOpen, onClose }: CustomerSubAccountsModalProps) => {
  if (!customer) return null;

  return (
    <SubAccountsModalContent
      key={customer.id}
      customer={customer}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

export default CustomerSubAccountsModal;
